// LlmBrandProviderPicker — per-brand LLM provider override, as a standalone
// table grouped by company. Each brand gets a <select> to pin a provider (or
// "Inherit" the global default). Self-contained: pulls the brand list from
// /api/admin/ai-ops/overview (which now carries each brand's provider pin) and
// the ready-provider list from the worker. Used on Super-Admin Settings.

import React, { useCallback, useEffect, useState } from "react";
import { get, post } from "../../api";

export default function LlmBrandProviderPicker() {
  const [companies, setCompanies] = useState([]);
  const [globalProvider, setGlobalProvider] = useState("siliconflow");
  const [textProviders, setTextProviders] = useState([]);
  const [imageProviders, setImageProviders] = useState([]);
  const [videoProviders, setVideoProviders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    try {
      const [ov, prov] = await Promise.all([
        get("/api/admin/ai-ops/overview"),
        get("/worker/admin/llm-providers"),
      ]);
      if (ov) { setCompanies(ov.companies || []); setGlobalProvider(ov.globalProvider || "siliconflow"); }
      if (prov && prov.data) { setTextProviders(prov.data.text || []); setImageProviders(prov.data.image || []); setVideoProviders(prov.data.video || []); }
    } catch (e) { /* non-fatal */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setBrand = async (brandId, brandName, provider, kind = "text") => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await post("/api/admin/flows/provider", { scope: "brand", provider, kind, brandId, brandName }, false);
      setMsg(`${brandName} ${kind} → ${provider === "inherit" ? "inherit (global)" : provider}`);
      await load();
    } catch (e) { setErr((e && (e.error || e.message)) || "Set provider failed"); }
    finally { setBusy(false); }
  };

  const f = filter.trim().toLowerCase();
  const shown = companies
    .map((c) => ({ ...c, brands: (c.brands || []).filter((b) => !f || (b.name || "").toLowerCase().includes(f) || (c.companyName || "").toLowerCase().includes(f)) }))
    .filter((c) => c.brands.length);

  return (
    <div className="lbp">
      <style>{`
        .lbp{font-size:13px}
        .lbp-field{padding:4px 8px;border:1px solid #d1d5db;border-radius:6px;font-size:12px}
        .lbp-co{font-weight:700;font-size:12px;background:#fafbfc;border:1px solid #f0f0f0;border-radius:6px 6px 0 0;padding:5px 10px}
        .lbp-rows{border:1px solid #f0f0f0;border-top:none;border-radius:0 0 6px 6px;margin-bottom:8px}
        .lbp-row{display:flex;align-items:center;gap:8px;padding:4px 10px;border-top:1px solid #f6f6f6;flex-wrap:wrap}
        .lbp-kind{display:inline-flex;flex-direction:column;font-size:9px;color:#94a3b8;gap:1px}
        .lbp-row:first-child{border-top:none}
        .lbp-name{flex:1;min-width:120px}
        .lbp-tag{font-size:10px;border-radius:999px;padding:1px 7px;color:#fff}
      `}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <input className="lbp-field" placeholder="filter brand / company…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 220 }} />
        <span style={{ fontSize: 11, color: "#64748b" }}>Global default: <b>{globalProvider}</b>. A pin overrides it for that brand's flows.</span>
      </div>
      {err && <div style={{ color: "#b91c1c", fontSize: 12, marginBottom: 6 }}>{err}</div>}
      {msg && <div style={{ color: "#047857", fontSize: 12, marginBottom: 6 }}>{msg}</div>}
      {shown.length === 0 && <div style={{ fontSize: 12, color: "#aaa" }}>No brands{filter ? " match the filter" : ""}.</div>}
      <div style={{ maxHeight: 420, overflow: "auto" }}>
        {shown.map((company) => (
          <div key={company.companyId}>
            <div className="lbp-co">{company.companyName} <span style={{ fontWeight: 400, color: "#94a3b8" }}>· {company.brands.length}</span></div>
            <div className="lbp-rows">
              {company.brands.map((b) => (
                <div className="lbp-row" key={b.brandId} style={b.disabled ? { opacity: 0.55 } : undefined}>
                  <span className="lbp-name">{b.name}{b.disabled && <span style={{ color: "#b91c1c", fontSize: 10 }}> (disabled)</span>}</span>
                  <label className="lbp-kind">Text
                    <select className="lbp-field" disabled={busy} value={b.provider || "inherit"} onChange={(e) => setBrand(b.brandId, b.name, e.target.value, "text")}>
                      <option value="inherit">Inherit ({globalProvider})</option>
                      {textProviders.map((p) => <option key={p.id} value={p.id} disabled={!p.ready}>{p.label}{p.ready ? "" : " (no key)"}</option>)}
                    </select>
                  </label>
                  <label className="lbp-kind">Image
                    <select className="lbp-field" disabled={busy} value={b.imageProvider || "inherit"} onChange={(e) => setBrand(b.brandId, b.name, e.target.value, "image")}>
                      <option value="inherit">Auto{b.effectiveImageProvider ? ` (${b.effectiveImageProvider})` : ""}</option>
                      {imageProviders.map((p) => <option key={p.id} value={p.id} disabled={!p.ready}>{p.label}{p.ready ? "" : " (no key)"}</option>)}
                    </select>
                  </label>
                  <label className="lbp-kind">Video
                    <select className="lbp-field" disabled={busy} value={b.videoProvider || "inherit"} onChange={(e) => setBrand(b.brandId, b.name, e.target.value, "video")}>
                      <option value="inherit">Auto{b.effectiveVideoProvider ? ` (${b.effectiveVideoProvider})` : ""}</option>
                      {videoProviders.map((p) => <option key={p.id} value={p.id} disabled={!p.ready}>{p.label}{p.ready ? "" : " (no key)"}</option>)}
                    </select>
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
